/**
 * The coloured weather field: which variable is loaded, for which model and view.
 *
 * Deliberately a second store rather than more state bolted onto realWeather. That
 * one owns wind speed AND direction because arrows need both, and it is read by
 * the auto-router, which has no business being disturbed by someone switching the
 * overlay to rain. This one owns a single number per point and nothing depends on
 * it but the wash.
 *
 * Cached on model + variable + rounded extent, NOT on the hour, so nudging along
 * the time bar re-slices what is already here and costs nothing. That is the same
 * rule as the wind field and for the same reason: scrubbing time is the thing a
 * skipper does most, and it should be instant.
 */
import { create } from 'zustand';
import type { LonLatBounds } from '@/store/chartSources';
import { modelById } from '@/weather/models';
import {
  fetchScalarField,
  fetchConsensusScalarField,
  gridAt,
  refusalMessage,
  type ScalarField,
} from '@/weather/scalarField';
import { fieldVariable } from '@/map/fieldRamps';
import type { FieldGrid } from '@/map/fieldRaster';

type Status = 'idle' | 'loading' | 'ready' | 'error';

interface WeatherFieldState {
  field: ScalarField | null;
  /** Which variable the cached field holds, so a stale ramp is never applied to it. */
  variableId: string | null;
  status: Status;
  error: string | null;
  key: string | null;
  load: (
    bounds: LonLatBounds,
    modelId: string,
    variableId: string,
    days: number,
  ) => Promise<void>;
  clear: () => void;
}

/** Does the loaded extent fully cover the asked-for one. */
function containsBounds(
  outer: [number, number, number, number],
  inner: [number, number, number, number],
): boolean {
  return outer[0] <= inner[0] && outer[1] <= inner[1] && outer[2] >= inner[2] && outer[3] >= inner[3];
}

function keyFor(bounds: LonLatBounds, modelId: string, variableId: string): string {
  const r = (n: number) => n.toFixed(2);
  return `${modelId}:${variableId}:${r(bounds[0])},${r(bounds[1])},${r(bounds[2])},${r(bounds[3])}`;
}

export const useWeatherFieldStore = create<WeatherFieldState>((set, get) => ({
  field: null,
  variableId: null,
  status: 'idle',
  error: null,
  key: null,

  load: async (bounds, modelId, variableId, days) => {
    const variable = fieldVariable(variableId);
    if (!variable) {
      set({ field: null, variableId: null, status: 'idle', error: null, key: null });
      return;
    }
    const key = keyFor(bounds, modelId, variableId);
    const cur = get();
    if (cur.key === key && (cur.status === 'ready' || cur.status === 'loading')) return;
    // The loaded field already covers the asked-for extent for this model and
    // variable: the view is moving around inside it (the caller fetches padded
    // bounds so this is the common case), and refetching would spend a request
    // to learn nothing. This, with the padding, is what stopped the wash
    // stopping at an invisible wall on every zoom-out (fb-msie7e90-0).
    // The consensus wash is IN this check on purpose, and being out of it was
    // fb-mslh25ky-0 ("why no weather data on the agreed model?"): the blend is
    // seven requests per fetch, so it was always the first model to hit the
    // server's rate limit, and every gesture refetched it. Its finished field
    // carries bounds like any other, so containment applies the same.
    if (
      cur.field &&
      cur.variableId === variableId &&
      cur.status === 'ready' &&
      cur.key?.startsWith(`${modelId}:${variableId}:`) &&
      containsBounds(cur.field.bounds, bounds)
    ) {
      return;
    }

    set({ key, status: 'loading', error: null });
    try {
      if (modelId === 'consensus') {
        const { field, used } = await fetchConsensusScalarField(
          bounds,
          variable.hourly,
          variable.outlierAt,
          days,
        );
        if (get().key !== key) return;
        set({
          field,
          variableId,
          status: 'ready',
          error: used.length < 2 ? `Only ${used.length} model answered here` : null,
        });
        return;
      }
      const field = await fetchScalarField(
        bounds,
        modelId,
        variable.hourly,
        days,
        undefined,
        variable.marine === true,
      );
      // The view or the variable moved on while we were waiting: this answer is
      // about somewhere we are no longer looking, so it is dropped rather than drawn.
      if (get().key !== key) return;
      set({ field, variableId, status: 'ready', error: null });
    } catch (e) {
      if (get().key !== key) return;
      const name = modelById(modelId)?.name ?? modelId;
      const msg = e instanceof Error ? e.message : '';
      // Three different failures, three different truths. A 429 is the server
      // asking us to slow down, and calling it "does not publish wind here"
      // (which is what happened on fb-msie8z8m-2's screen) sends the skipper
      // hunting through models for a problem that fixes itself in a minute. A
      // real refusal means the variable genuinely is not there, so the stale
      // wash comes down. Anything else is a network blip, and a stale wash
      // with an honest note beats a blank chart.
      const rateLimited = msg.includes('429');
      const refusal = !rateLimited && msg.startsWith('HTTP 4');
      const keepStale = !refusal && get().variableId === variableId;
      set({
        field: keepStale ? get().field : null,
        status: 'error',
        error: rateLimited
          ? 'The weather server asked us to slow down. Give it a minute, then nudge the map.'
          : refusal
            ? refusalMessage(name, variable.name)
            : `Could not load ${variable.name} (${msg || 'no answer'})`,
      });
    }
  },

  clear: () => set({ field: null, variableId: null, status: 'idle', error: null, key: null }),
}));

/** The grid to draw right now, or null if there is nothing loaded for this variable. */
export function fieldGridAt(
  state: Pick<WeatherFieldState, 'field' | 'variableId'>,
  variableId: string,
  targetMs: number,
): FieldGrid | null {
  if (!state.field || state.variableId !== variableId) return null;
  return gridAt(state.field, targetMs);
}
